import React, { memo, useCallback, useEffect, useRef, useState } from 'react'

import { TrackEventName } from '../../../../../modules/analytics/events'
import { track } from '../../../../../modules/analytics/utils'
import { useJoystick } from './hooks'

import './SnakeGame.css'

type Point = {
  x: number
  y: number
}

const GRID_SIZE: Point = {
  x: 16,
  y: 8,
}

// point size (in px) // TODO adjust based on device size?
const POINT_SIZE = 10

const initialFood: Point = {
  x: Math.floor(GRID_SIZE.x / 2),
  y: Math.floor(GRID_SIZE.y / 2),
}

const startPoint = { x: 0, y: 0 }

export type Direction = 'up' | 'down' | 'right' | 'left'

const gameUrlParam = new URLSearchParams(window.location.search).get('minigame')
const isGameEnabled = gameUrlParam === 'true' || gameUrlParam === '1'

const SnakeGame: React.FC = () => {
  const [gameStarted, setGameStarted] = useState(false)
  const [snakeBody, setSnakeBody] = useState<Point[]>([startPoint])
  const [food, setFood] = useState<Point>(initialFood)
  const [direction, setDirection] = useState<Direction>('right')
  const [gameOver, setGameOver] = useState(false)
  const [score, setScore] = useState(0)

  const createFood = useCallback(() => {
    const newFood: Point = {
      x: Math.floor(Math.random() * GRID_SIZE.x),
      y: Math.floor(Math.random() * GRID_SIZE.y),
    }
    setFood(newFood)
  }, [])

  const [round, setRound] = useState(0)

  const trackGameEvent = useCallback(
    (type: 'start' | 'end') => {
      track(TrackEventName.MINIGAME_EVENT, {
        location: 'ConnectStatus SnakeGame',
        round,
        score,
        type,
      })
    },
    [round, score],
  )

  const startGame = useCallback(() => {
    trackGameEvent('start')
    setGameOver(false)
    setDirection('right')
    setSnakeBody([startPoint])
    setScore(0)
    createFood()
    setGameStarted(true)
  }, [createFood, trackGameEvent])

  // handle direction changes on touch swipe/arrow key button press
  useJoystick(
    (direction_: Direction) => {
      if (!isGameEnabled) {
        return
      }

      if (!gameStarted) {
        startGame()
        return
      }
      if (gameOver) {
        startGame()
        return
      }

      switch (direction_) {
        case 'up': {
          setDirection((previous) => (previous !== 'down' ? 'up' : previous))
          break
        }
        case 'down': {
          setDirection((previous) => (previous !== 'up' ? 'down' : previous))
          break
        }
        case 'left': {
          setDirection((previous) => (previous !== 'right' ? 'left' : previous))
          break
        }
        case 'right': {
          setDirection((previous) => (previous !== 'left' ? 'right' : previous))
          break
        }
      }
    },
    [gameOver, gameStarted, startGame],
  )

  const gameIntervalRef = useRef<number>()

  const gameUpdate = useCallback(() => {
    const [{ x, y }] = snakeBody
    let newHead: Point

    switch (direction) {
      case 'up':
        newHead = { x, y: y - 1 }
        break
      case 'down':
        newHead = { x, y: y + 1 }
        break
      case 'left':
        newHead = { x: x - 1, y }
        break
      case 'right':
        newHead = { x: x + 1, y }
        break
      default:
        newHead = { x, y }
    }

    // check wall collision
    const isWallCollision =
      newHead.x < 0 ||
      newHead.x > GRID_SIZE.x ||
      newHead.y < 0 ||
      newHead.y > GRID_SIZE.y

    // Check if the new head collides with the snake body
    const isOwnBodyCollision = snakeBody
      .slice(1)
      .find(({ x: x_, y: y_ }) => x_ === newHead.x && y_ === newHead.y)

    if (isWallCollision || isOwnBodyCollision) {
      // game over
      setGameOver(true)
      setRound((prevRound) => prevRound + 1)
      trackGameEvent('end')
      clearInterval(gameIntervalRef.current)
      return
    }

    const isFoodCollision = newHead.x === food.x && newHead.y === food.y

    if (isFoodCollision) {
      // eat food
      createFood()
      setScore(score + 1)
      // move and grow 1: update adding newHead, while retaining the entire tail
      setSnakeBody([newHead, ...snakeBody])
      return
    }

    // no collision
    // update setting new head, "removing" it from the last block of the tail
    setSnakeBody([newHead, ...snakeBody.slice(0, -1)])
  }, [createFood, direction, food.x, food.y, score, snakeBody, trackGameEvent])

  // Game update loop
  useEffect(() => {
    if (!gameStarted || gameOver) {
      return
    }

    const intervalId = window.setInterval(gameUpdate, 200)
    gameIntervalRef.current = intervalId

    // disabling eslint error because it's a false positive in this case
    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalId)
    }
  }, [gameOver, gameStarted, gameUpdate])

  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current

    if (!gameStarted || !canvas) {
      return
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    // game-over overlay
    if (gameOver) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = 'white'
      ctx.textAlign = 'center'
      ctx.fillText('Game Over!', canvas.width / 2, canvas.height / 2 - 10)
      ctx.fillText(
        'Press any arrow key to play again',
        canvas.width / 2,
        canvas.height / 2 + 10,
      )
      return
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Draw snake
    snakeBody.forEach(({ x, y }) => {
      ctx.fillStyle = 'green'
      ctx.fillRect(x * POINT_SIZE, y * POINT_SIZE, POINT_SIZE, POINT_SIZE)
    })

    // Draw food
    ctx.fillStyle = 'red'
    ctx.fillRect(
      food.x * POINT_SIZE,
      food.y * POINT_SIZE,
      POINT_SIZE,
      POINT_SIZE,
    )
  }, [gameStarted, gameOver, snakeBody, food])

  return (
    <div className={'SnakeGame'}>
      {gameStarted && (
        <div>
          <div className={'game-panel'}>
            <span>Score: {score}</span>
          </div>

          <div className={'canvas-container'}>
            <canvas
              className={'snake-canvas'}
              ref={canvasRef}
              width={(GRID_SIZE.x + 1) * POINT_SIZE}
              height={(GRID_SIZE.y + 1) * POINT_SIZE}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(SnakeGame)
